import { useState, useEffect } from "react";
import { useLazyGetBoundDatesQuery } from "../../services/blotterApi";
import type { BlotterFeedType } from "../../services/blotterApi";
import dayjs, { Dayjs } from "dayjs";
import utcPlugin from "dayjs/plugin/utc";

dayjs.extend(utcPlugin);

/**
 * Hook to manage date picker state with bounds and debouncing.
 *
 * Features:
 * - Fetches min/max date bounds from API
 * - Manages start date selection
 * - Provides historical timestamp for display
 *
 * Performance:
 * - Date changes are NOT debounced here (handled by parent useBlotterData hook)
 * - Bound dates are fetched once per account/feed type combination
 */
export const useDateRangePicker = (accountId: string | null, feedType: BlotterFeedType) => {
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [historicalTimestamp, setHistoricalTimestamp] = useState<string | null>(null);
  const [boundDates, setBoundDates] = useState<{ min: Dayjs | null; max: Dayjs | null }>({
    min: null,
    max: null,
  });

  // Lazy query for fetching bound dates
  const [fetchBoundDates, { data: boundDatesData, isLoading: boundDatesLoading }] = useLazyGetBoundDatesQuery();

  // Fetch bound dates when account or feed type changes
  useEffect(() => {
    if (accountId && feedType) {
      fetchBoundDates({ account_id: accountId, feed_type: feedType });
    }
  }, [accountId, feedType, fetchBoundDates]);

  // Update bound dates when data arrives
  useEffect(() => {
    if (boundDatesData) {
      const min = boundDatesData.min ? dayjs(boundDatesData.min).utc() : null;
      const max = boundDatesData.max ? dayjs(boundDatesData.max).utc() : null;

      setBoundDates({ min, max });
    }
  }, [boundDatesData]);

  // Update historical timestamp when start date changes
  useEffect(() => {
    if (startDate) {
      setHistoricalTimestamp(startDate.format("YYYY-MM-DD HH:mm:ss"));
    } else {
      setHistoricalTimestamp(null);
    }
  }, [startDate]);

  // Reset start date when account or feed type changes
  useEffect(() => {
    setStartDate(null);
    setHistoricalTimestamp(null);
  }, [accountId, feedType]);

  return {
    startDate,
    setStartDate,
    boundDates,
    historicalTimestamp,
    boundDatesLoading,
  };
};
